import styled from 'styled-components';

interface ProgressBarProps {
  variant?: 'default' | 'success' | 'warning' | 'error';
  size?: 'small' | 'medium' | 'large';
  progress?: number;
  animated?: boolean;
  striped?: boolean;
}

const getProgressColors = (variant: string) => {
  switch (variant) {
    case 'success':
      return {
        background: '#dcfce7',
        fill: '#10b981'
      };
    case 'warning':
      return {
        background: '#fef3c7',
        fill: '#f59e0b'
      };
    case 'error':
      return {
        background: '#fee2e2',
        fill: '#ef4444'
      };
    case 'default':
    default:
      return {
        background: '#e5e7eb',
        fill: '#667eea'
      };
  }
};

export const ProgressContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

export const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
`;

export const ProgressText = styled.span`
  font-weight: 500;
`;

export const ProgressPercentage = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
`;

export const ProgressTrack = styled.div<ProgressBarProps>`
  width: 100%;
  height: ${props => {
    switch (props.size) {
      case 'small': return '4px';
      case 'large': return '12px';
      default: return '8px';
    }
  }};
  background: ${props => getProgressColors(props.variant || 'default').background};
  border-radius: ${props => {
    switch (props.size) {
      case 'small': return '2px';
      case 'large': return '6px';
      default: return '4px';
    }
  }};
  overflow: hidden;
  position: relative;
`;

export const ProgressFill = styled.div<ProgressBarProps>`
  height: 100%;
  width: ${props => Math.min(Math.max(props.progress || 0, 0), 100)}%;
  background: ${props => getProgressColors(props.variant || 'default').fill};
  border-radius: inherit;
  transition: width 0.3s ease;
  position: relative;
  
  ${props => props.striped && `
    background-image: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.15) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.15) 50%,
      rgba(255, 255, 255, 0.15) 75%,
      transparent 75%,
      transparent
    );
    background-size: 1rem 1rem;
  `}
  
  ${props => props.animated && `
    animation: progress-bar-stripes 1s linear infinite;
    
    @keyframes progress-bar-stripes {
      0% {
        background-position: 1rem 0;
      }
      100% {
        background-position: 0 0;
      }
    }
  `}
`;

export const CircularProgressContainer = styled.div<{ size?: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.size || 40}px;
  height: ${props => props.size || 40}px;
`;

export const CircularProgressSvg = styled.svg<{ size?: number }>`
  width: ${props => props.size || 40}px;
  height: ${props => props.size || 40}px;
  transform: rotate(-90deg);
`;

export const CircularProgressTrack = styled.circle<ProgressBarProps>`
  fill: none;
  stroke: ${props => getProgressColors(props.variant || 'default').background};
  stroke-width: 3;
`;

export const CircularProgressFill = styled.circle<ProgressBarProps & { circumference?: number }>`
  fill: none;
  stroke: ${props => getProgressColors(props.variant || 'default').fill};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: ${props => props.circumference || 0};
  stroke-dashoffset: ${props => {
    const progress = Math.min(Math.max(props.progress || 0, 0), 100);
    const circumference = props.circumference || 0;
    return circumference - (progress / 100) * circumference;
  }};
  transition: stroke-dashoffset 0.3s ease;
`;

// Specialized progress variants
export const SuccessProgress = styled(ProgressFill).attrs({ variant: 'success' })``;
export const WarningProgress = styled(ProgressFill).attrs({ variant: 'warning' })``;
export const ErrorProgress = styled(ProgressFill).attrs({ variant: 'error' })``;

export const IndeterminateProgress = styled(ProgressTrack)`
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 30%;
    background: ${props => getProgressColors(props.variant || 'default').fill};
    border-radius: inherit;
    animation: indeterminate 2s ease-in-out infinite;
  }
  
  @keyframes indeterminate {
    0% {
      left: -30%;
    }
    100% {
      left: 100%;
    }
  }
`;